import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Minus, Plus, Trash2 } from "lucide-react";
import { eventServices, cleaningServices, gardenServices } from "../data/services";
import { useLanguage } from "../i18n/LanguageContext";
import { loadCartItems, saveCartItems } from "../utils/cart";

const ALL_OPTIONS = (() => {
  const opts = [];
  cleaningServices.forEach((s) =>
    s.groups?.forEach((g) =>
      g.items.forEach((it) => {
        const useGroup = s.groups.length > 1 && g.title !== s.name;
        const label = useGroup ? `${s.name} — ${g.title} — ${it.label}` : `${s.name} — ${it.label}`;
        const labelBn = useGroup
          ? `${s.nameBn} — ${g.titleBn || g.title} — ${it.labelBn || it.label}`
          : `${s.nameBn} — ${it.labelBn || it.label}`;
        opts.push({ key: label, label, labelBn, price: it.price, category: "Cleaning Services" });
      })
    )
  );
  eventServices.forEach((s) =>
    s.packages?.forEach((p) =>
      opts.push({
        key: `${s.name} — ${p.label}`,
        label: `${s.name} — ${p.label}`,
        labelBn: `${s.nameBn} — ${p.labelBn || p.label}`,
        price: p.price,
        category: "Surprise Event Management",
      })
    )
  );
  gardenServices.forEach((s) =>
    s.packages?.forEach((p) =>
      opts.push({
        key: `${s.name} — ${p.label}`,
        label: `${s.name} — ${p.label}`,
        labelBn: `${s.nameBn} — ${p.labelBn || p.label}`,
        price: p.price,
        category: "Garden & Plant Care",
      })
    )
  );
  return opts;
})();

export default function BookingPage() {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [items, setItems] = useState(loadCartItems);
  const [showAdd, setShowAdd] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const total = items.reduce((s, i) => s + (i.price || 0) * i.qty, 0);
  const count = items.reduce((s, i) => s + i.qty, 0);

  const setQty = (key, delta) =>
    setItems((its) =>
      its.map((i) => (i.key === key ? { ...i, qty: i.qty + delta } : i)).filter((i) => i.qty > 0)
    );

  const removeItem = (key) => setItems((its) => its.filter((i) => i.key !== key));

  const addOption = (opt) =>
    setItems((its) => {
      const found = its.find((i) => i.key === opt.key);
      if (found) return its.map((i) => (i.key === opt.key ? { ...i, qty: i.qty + 1 } : i));
      return [...its, { ...opt, qty: 1 }];
    });

  const goToForm = () => {
    saveCartItems(items);
    navigate("/booking-form");
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-4" data-testid="booking-page">
        <div className="rounded-3xl bg-white border border-stone-200 p-10 text-center max-w-md">
          <p className="text-lg text-stone-600">{t.page.empty}</p>
          <Link
            to="/"
            data-testid="bp-back-empty"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#E85D75] text-white font-semibold px-6 py-3"
          >
            <ArrowLeft className="w-4 h-4" /> {t.page.backHome}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6] text-stone-900" data-testid="booking-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
        <div className="flex items-center justify-between mb-8">
          <Link
            to="/"
            data-testid="bp-back-home"
            className="inline-flex items-center gap-2 text-base font-medium text-stone-600 hover:text-[#E85D75] transition-colors"
          >
            <ArrowLeft className="w-5 h-5" /> {t.page.backHome}
          </Link>
          <img src="/assets/logo.png" alt="Alpha & More" className="h-11 w-11 rounded-full object-cover" />
        </div>

        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
          {t.page.yourSelection}
        </h1>

        <div className="mt-6 space-y-3">
          {items.map((i, idx) => (
            <div
              key={i.key}
              data-testid={`selection-item-${idx}`}
              className="rounded-2xl bg-white border border-stone-200 px-5 py-4 flex items-center justify-between gap-4"
            >
              <div className="min-w-0">
                <p className="text-base sm:text-lg font-semibold text-stone-900 leading-snug">
                  {(lang === "bn" && i.labelBn) || i.label}
                </p>
                <p className="text-sm text-stone-500 mt-0.5">
                  {i.price != null ? `₹${i.price.toLocaleString("en-IN")}` : t.priceOnEnquiry}
                </p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {i.price != null && (
                  <div className="flex items-center gap-3 bg-stone-100 rounded-full p-1">
                    <button
                      onClick={() => setQty(i.key, -1)}
                      data-testid={`sel-minus-${idx}`}
                      aria-label="Decrease"
                      className="w-9 h-9 rounded-full bg-white shadow-sm flex items-center justify-center text-stone-700"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="w-6 text-center text-base font-bold" data-testid={`sel-qty-${idx}`}>
                      {i.qty}
                    </span>
                    <button
                      onClick={() => setQty(i.key, 1)}
                      data-testid={`sel-plus-${idx}`}
                      aria-label="Increase"
                      className="w-9 h-9 rounded-full bg-white shadow-sm flex items-center justify-center text-stone-700"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                )}
                <button
                  onClick={() => removeItem(i.key)}
                  data-testid={`sel-remove-${idx}`}
                  aria-label="Remove"
                  className="w-9 h-9 rounded-full bg-red-50 text-red-500 flex items-center justify-center hover:bg-red-100"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 flex items-center justify-between rounded-2xl bg-stone-900 text-white px-5 py-4">
          <span className="text-base font-medium">
            {count} {t.itemsWord}
          </span>
          <span className="text-xl font-bold" data-testid="page-total">
            ₹{total.toLocaleString("en-IN")}
          </span>
        </div>

        <div className="mt-5 flex flex-wrap gap-3">
          <button
            onClick={() => setShowAdd((s) => !s)}
            data-testid="add-more-button"
            className="rounded-full bg-[#E85D75] text-white text-base font-semibold px-6 py-3 hover:bg-[#d64a63] transition-colors"
          >
            {t.page.addMore}
          </button>
          <button
            onClick={goToForm}
            data-testid="page-continue-button"
            className="rounded-full bg-stone-900 text-white text-base font-semibold px-6 py-3 hover:bg-stone-700 transition-colors"
          >
            {t.page.continue}
          </button>
        </div>

        {showAdd && (
          <div className="mt-6" data-testid="add-options-panel">
            <div className="grid sm:grid-cols-2 gap-3">
              {ALL_OPTIONS.map((opt, idx) => (
                <div
                  key={opt.key}
                  data-testid={`add-option-${idx}`}
                  className="rounded-2xl bg-amber-100 border border-amber-300 px-4 py-3 flex items-center justify-between gap-3"
                >
                  <div className="min-w-0">
                    <p className="text-sm sm:text-base font-semibold text-stone-800 leading-snug">
                      {(lang === "bn" && opt.labelBn) || opt.label}
                    </p>
                    <p className="text-base font-bold text-amber-700">
                      ₹{opt.price.toLocaleString("en-IN")}
                    </p>
                  </div>
                  <button
                    onClick={() => addOption(opt)}
                    data-testid={`add-option-btn-${idx}`}
                    className="shrink-0 rounded-full bg-white border border-amber-300 text-stone-800 text-sm font-semibold px-4 py-2 hover:bg-amber-50"
                  >
                    + {t.page.add}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
